/**
 * Accepting or declining an offer from the public link.
 *
 * The buttons are drawn from `answer.answerable`, which the SERVER computed.
 * Nothing here re-derives it: an offer that lapsed in the spring still reads
 * `sent` in the database, so a browser deciding for itself would draw an Accept
 * button the API then refuses - and the refusal would arrive after the client
 * had already believed they accepted.
 *
 * The name field is not identity and is not treated as one. Anybody holding the
 * link can type anything into it; what it is for is the record of who said they
 * pressed the button, which is why the server keeps it with the address and the
 * minute beside it. That is also why a decline asks for a reason and an accept
 * does not: a refusal without its reason is the half nobody can act on.
 */
import { useState } from "react";
import { Check, Loader2, X } from "lucide-react";
import { Badge, Button, Card, CardContent, Input, Label, Textarea } from "@wa/ui";
import type { EstimateDecision } from "@wa/core/estimate-answer";
import { useT, useI18n } from "../../i18n/I18nProvider";
import { fmtDateTime } from "../../lib/format";
import { publicDoc, type PublicDoc, type PublicDocResponse } from "../../lib/publicDoc";

export function DocDecision({
  token,
  doc,
  onAnswered,
}: {
  token: string;
  doc: PublicDoc;
  onAnswered: (next: PublicDocResponse) => void;
}) {
  const t = useT();
  const { locale } = useI18n();
  const answer = doc.answer;
  const [picked, setPicked] = useState<EstimateDecision | null>(null);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!answer) return null;

  // Already answered: print who and when, and draw nothing to press.
  if (answer.state === "answered") {
    const d = answer.decision;
    const accepted = (d?.decision ?? doc.status) === "accepted";
    return (
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-wrap items-center gap-3">
            <Badge variant={accepted ? "success" : "secondary"}>
              {t(accepted ? "publicDoc.decision.acceptedBadge" : "publicDoc.decision.declinedBadge")}
            </Badge>
            {d?.name ? (
              <span className="text-sm text-muted-foreground">
                {t(accepted ? "publicDoc.decision.acceptedBy" : "publicDoc.decision.declinedBy", { name: d.name })}
                {d.at ? ` - ${fmtDateTime(d.at, locale)}` : ""}
              </span>
            ) : null}
          </div>
          {d?.note ? <p className="mt-3 whitespace-pre-wrap text-sm text-foreground">{d.note}</p> : null}
        </CardContent>
      </Card>
    );
  }

  if (answer.state === "expired") {
    return (
      <Card>
        <CardContent className="pt-6">
          <p className="text-sm text-muted-foreground">{t("publicDoc.decision.expired")}</p>
        </CardContent>
      </Card>
    );
  }

  if (!answer.answerable) return null;

  const submit = async () => {
    if (!picked || !name.trim()) return;
    setBusy(true);
    setError(null);
    try {
      const next = await publicDoc.answer(token, {
        decision: picked,
        name: name.trim(),
        ...(email.trim() ? { email: email.trim() } : {}),
        ...(picked === "declined" && note.trim() ? { note: note.trim() } : {}),
      });
      onAnswered(next);
    } catch {
      // Either the offer stopped being answerable between the page load and the
      // press, or the network went. Both need the same thing from the client: a
      // reload, which is what the message asks for.
      setError(t("publicDoc.decision.failed"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card>
      <CardContent className="pt-6">
        <h2 className="text-base font-semibold text-foreground">{t("publicDoc.decision.title")}</h2>
        <p className="mt-1 text-sm text-muted-foreground">{t("publicDoc.decision.hint")}</p>

        <div className="mt-4 flex flex-wrap gap-2">
          <Button
            variant={picked === "accepted" ? "primary" : "outline"}
            onClick={() => setPicked("accepted")}
          >
            <Check className="mr-2 size-4" />
            {t("publicDoc.decision.accept")}
          </Button>
          <Button
            variant={picked === "declined" ? "destructive" : "outline"}
            onClick={() => setPicked("declined")}
          >
            <X className="mr-2 size-4" />
            {t("publicDoc.decision.decline")}
          </Button>
        </div>

        {picked ? (
          <div className="mt-5 space-y-4 border-t border-border pt-5">
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <Label htmlFor="pd-name">{t("publicDoc.field.name")}</Label>
                <Input id="pd-name" value={name} onChange={(e) => setName(e.target.value)} autoComplete="name" />
              </div>
              <div>
                <Label htmlFor="pd-email">{t("publicDoc.field.email")}</Label>
                <Input id="pd-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="email" />
              </div>
            </div>

            {picked === "declined" ? (
              <div>
                <Label htmlFor="pd-note">{t("publicDoc.decision.reason")}</Label>
                <Textarea id="pd-note" rows={3} value={note} onChange={(e) => setNote(e.target.value)} />
                <p className="mt-1 text-xs text-muted-foreground">{t("publicDoc.decision.reasonHint")}</p>
              </div>
            ) : null}

            {error ? <p className="text-sm text-destructive">{error}</p> : null}

            <Button onClick={submit} disabled={busy || !name.trim()}>
              {busy ? <Loader2 className="mr-2 size-4 animate-spin" /> : null}
              {t(picked === "accepted" ? "publicDoc.decision.confirmAccept" : "publicDoc.decision.confirmDecline")}
            </Button>
          </div>
        ) : null}
      </CardContent>
    </Card>
  );
}
